import React, { useEffect, useState } from "react";
import { X, Trash2, Loader2, Monitor, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  crearPrograma, actualizarPrograma, eliminarPrograma,
  subirInstaladorPrograma, borrarInstaladorPrograma,
  ESTADOS_PROGRAMA, TIPOS_PROGRAMA, ICONOS_PROGRAMA,
} from "../../lib/programasSoftware";
import { actualizarPreciosPrograma } from "../../lib/licenciasAdmin";

const EMPTY_DIST = {
  enabled: false, release_status: "", distribution_type: "", version: "",
  architecture: "", download_url: "", file_size: "", sha256: "",
  requirements: "", changelog: "", published_at: "",
  installer_filename: "", tiene_instalador: false,
};

const EMPTY = {
  name: "", description: "", functional_desc: "", type: "SPECIALIZED_PROGRAM",
  icon: "Boxes", image_url: "", capabilities: "", status: "PROXIMAMENTE",
  display_order: 99, label: "", app_url: "", api_url: "", docs_url: "",
  is_featured: false, visible: true,
  // PASO 2C · Licencia del programa (nivel programa). max_devices editable; licenciable/precios solo lectura aquí.
  max_devices: 1, licenciable: false, precios: null,
  // PASO 2B · Distribución multiplataforma
  distributions: { windows: { ...EMPTY_DIST }, android: { ...EMPTY_DIST } },
};

const DIST_LABEL = { uploaded_file: "Archivo subido a la plataforma", external_url: "URL externa (HTTPS)" };
const RELEASE_LABEL = {
  draft: "Borrador", available: "Disponible", updating: "Actualizando",
  maintenance: "Mantenimiento", coming_soon: "Próximamente",
};
const PLATFORMS = [
  { key: "windows", label: "Windows", Icon: Monitor, fileHint: "Instalador .exe / .msi" },
  { key: "android", label: "Android", Icon: Smartphone, fileHint: "APK .apk" },
];

const ESTADO_LABEL = {
  PROXIMAMENTE: "Próximamente", DISPONIBLE: "Disponible", BETA: "Beta", NO_DISPONIBLE: "No disponible",
};
const TIPO_LABEL = { MASTER_PROGRAM: "Programa Maestro", SPECIALIZED_PROGRAM: "Programa Especializado" };

const centToSoles = (cent) => (cent == null ? "" : (Number(cent) / 100).toString());
const PRECIO_KEYS = [
  { k: "m1", label: "1 mes" },
  { k: "m3", label: "3 meses" },
  { k: "m6", label: "6 meses" },
  { k: "m12", label: "12 meses" },
];

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const inputCls =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20";

const coerceDist = (d) => ({
  ...EMPTY_DIST,
  ...(d || {}),
  enabled: !!(d && d.enabled),
  release_status: (d && d.release_status) || "",
  distribution_type: (d && d.distribution_type) || "",
  version: (d && d.version) || "",
  architecture: (d && d.architecture) || "",
  download_url: (d && d.download_url) || "",
  file_size: d && d.file_size != null ? d.file_size : "",
  sha256: (d && d.sha256) || "",
  requirements: (d && d.requirements) || "",
  changelog: (d && d.changelog) || "",
  published_at: (d && d.published_at) || "",
  installer_filename: (d && d.installer_filename) || "",
  tiene_instalador: !!(d && d.tiene_instalador),
});

const distPayload = (d) => ({
  enabled: !!d.enabled,
  release_status: d.release_status || null,
  distribution_type: d.distribution_type || null,
  version: d.version || null,
  architecture: d.architecture || null,
  download_url: d.download_url || null,
  file_size: d.file_size === "" || d.file_size === null ? null : Number(d.file_size),
  sha256: d.sha256 || null,
  requirements: d.requirements || null,
  changelog: d.changelog || null,
  published_at: d.published_at || null,
});

export const ProgramaFormModal = ({ open, onClose, onSaved, programa }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ windows: 0, android: 0 });
  const [preciosSoles, setPreciosSoles] = useState({ m1: "", m3: "", m6: "", m12: "" });
  const editing = !!programa;

  useEffect(() => {
    if (!open) return;
    if (programa) {
      const d = programa.distributions || {};
      setForm({
        ...EMPTY,
        ...programa,
        image_url: programa.image_url || "",
        label: programa.label || "",
        app_url: programa.app_url || "",
        api_url: programa.api_url || "",
        docs_url: programa.docs_url || "",
        capabilities: (programa.capabilities || []).join("\n"),
        max_devices: programa.max_devices ?? 1,
        licenciable: !!programa.licenciable,
        precios: programa.precios || null,
        distributions: { windows: coerceDist(d.windows), android: coerceDist(d.android) },
      });
      const p = programa.precios || {};
      setPreciosSoles({ m1: centToSoles(p.m1), m3: centToSoles(p.m3), m6: centToSoles(p.m6), m12: centToSoles(p.m12) });
    } else {
      setForm({ ...EMPTY, distributions: { windows: { ...EMPTY_DIST }, android: { ...EMPTY_DIST } } });
      setPreciosSoles({ m1: "", m3: "", m6: "", m12: "" });
    }
  }, [open, programa]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDist = (plat, k, v) =>
    setForm((f) => ({ ...f, distributions: { ...f.distributions, [plat]: { ...f.distributions[plat], [k]: v } } }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    let preciosCent = null;
    if (form.licenciable) {
      preciosCent = {
        m1: Math.round(parseFloat(preciosSoles.m1) * 100),
        m3: Math.round(parseFloat(preciosSoles.m3) * 100),
        m6: Math.round(parseFloat(preciosSoles.m6) * 100),
        m12: Math.round(parseFloat(preciosSoles.m12) * 100),
      };
      if ([preciosCent.m1, preciosCent.m3, preciosCent.m6, preciosCent.m12].some((v) => !Number.isInteger(v) || v <= 0)) {
        toast.error("Ingresa precios en soles válidos (> 0) para 1, 3, 6 y 12 meses");
        return;
      }
    }
    setSaving(true);
    const payload = {
      ...form,
      display_order: Number(form.display_order) || 99,
      label: form.label || null,
      image_url: form.image_url || null,
      app_url: form.app_url || null,
      api_url: form.api_url || null,
      docs_url: form.docs_url || null,
      capabilities: form.capabilities.split("\n").map((s) => s.trim()).filter(Boolean),
      max_devices: Number(form.max_devices) > 0 ? Number(form.max_devices) : 1,
      licenciable: !!form.licenciable,
      precios: preciosCent,
      distributions: {
        windows: distPayload(form.distributions.windows),
        android: distPayload(form.distributions.android),
      },
    };
    try {
      if (editing) {
        await actualizarPrograma(programa.id, payload);
        // Precios se persisten por su endpoint dedicado (el PUT general no los toca).
        if (form.licenciable && preciosCent) await actualizarPreciosPrograma(programa.id, preciosCent);
        toast.success("Programa actualizado");
      } else {
        await crearPrograma(payload);
        toast.success("Programa agregado");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm(`¿Eliminar "${programa.name}"? Esta acción no se puede deshacer.`)) return;
    setSaving(true);
    try {
      await eliminarPrograma(programa.id);
      toast.success("Programa eliminado");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setSaving(false);
    }
  };

  const handleInstaller = async (plat, file) => {
    if (!editing) { toast.error("Guarda el programa primero para subir el instalador"); return; }
    if (!file) return;
    setUploading((u) => ({ ...u, [plat]: 1 }));
    try {
      const updated = await subirInstaladorPrograma(programa.id, plat, file, (p) => setUploading((u) => ({ ...u, [plat]: p || 1 })));
      const nd = (updated.distributions || {})[plat] || {};
      setForm((f) => ({
        ...f,
        distributions: {
          ...f.distributions,
          [plat]: {
            ...f.distributions[plat],
            file_size: nd.file_size ?? "",
            sha256: nd.sha256 || "",
            installer_filename: nd.installer_filename || "",
            tiene_instalador: true,
          },
        },
      }));
      toast.success("Instalador subido");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el instalador");
    } finally {
      setUploading((u) => ({ ...u, [plat]: 0 }));
    }
  };

  const handleRemoveInstaller = async (plat) => {
    if (!editing) return;
    setUploading((u) => ({ ...u, [plat]: 1 }));
    try {
      await borrarInstaladorPrograma(programa.id, plat);
      setForm((f) => ({
        ...f,
        distributions: { ...f.distributions, [plat]: { ...f.distributions[plat], installer_filename: "", tiene_instalador: false } },
      }));
      toast.success("Instalador eliminado");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el instalador");
    } finally {
      setUploading((u) => ({ ...u, [plat]: 0 }));
    }
  };

  const renderPlatform = ({ key, label, Icon, fileHint }) => {
    const d = form.distributions[key];
    const up = uploading[key];
    return (
      <div key={key} className="rounded-lg border border-slate-200 bg-white p-3.5" data-testid={`dist-${key}`}>
        <div className="mb-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-800">
            <Icon size={16} className="text-brand-blue" /> {label}
          </span>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
            <input type="checkbox" checked={d.enabled} onChange={(e) => setDist(key, "enabled", e.target.checked)} data-testid={`dist-${key}-enabled`} />
            Habilitado
          </label>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Estado">
            <select className={inputCls} value={d.release_status} onChange={(e) => setDist(key, "release_status", e.target.value)} data-testid={`dist-${key}-status`}>
              <option value="">— Sin definir —</option>
              {Object.keys(RELEASE_LABEL).map((k) => <option key={k} value={k}>{RELEASE_LABEL[k]}</option>)}
            </select>
          </Field>
          <Field label="Tipo de distribución">
            <select className={inputCls} value={d.distribution_type} onChange={(e) => setDist(key, "distribution_type", e.target.value)} data-testid={`dist-${key}-type`}>
              <option value="">— Sin distribución —</option>
              {Object.keys(DIST_LABEL).map((k) => <option key={k} value={k}>{DIST_LABEL[k]}</option>)}
            </select>
          </Field>
          <Field label="Versión">
            <input className={inputCls} value={d.version} onChange={(e) => setDist(key, "version", e.target.value)} data-testid={`dist-${key}-version`} placeholder="1.0.0" />
          </Field>
          <Field label="Arquitectura">
            <input className={inputCls} value={d.architecture} onChange={(e) => setDist(key, "architecture", e.target.value)} data-testid={`dist-${key}-arch`} placeholder={key === "android" ? "arm64-v8a" : "x64"} />
          </Field>
          <Field label="Tamaño (bytes)" hint="Se autocompleta al subir un archivo.">
            <input type="number" className={inputCls} value={d.file_size} onChange={(e) => setDist(key, "file_size", e.target.value)} data-testid={`dist-${key}-filesize`} />
          </Field>
          <Field label="Fecha de publicación">
            <input className={inputCls} value={d.published_at} onChange={(e) => setDist(key, "published_at", e.target.value)} data-testid={`dist-${key}-publishedat`} placeholder="2026-06-01" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="SHA-256" hint="Se calcula automáticamente al subir un archivo.">
              <input className={inputCls} value={d.sha256} onChange={(e) => setDist(key, "sha256", e.target.value)} data-testid={`dist-${key}-sha256`} />
            </Field>
          </div>

          {d.distribution_type === "external_url" && (
            <div className="sm:col-span-2">
              <Field label="URL de descarga (HTTPS)" hint="Genérica. La URL real (p. ej. Cloudflare R2) se colocará después.">
                <input className={inputCls} value={d.download_url} onChange={(e) => setDist(key, "download_url", e.target.value)} data-testid={`dist-${key}-url`} placeholder="https://..." />
              </Field>
            </div>
          )}

          {d.distribution_type === "uploaded_file" && (
            <div className="sm:col-span-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/70 p-3">
              <span className="mb-1 block text-xs font-semibold text-slate-600">Archivo ({fileHint})</span>
              {!editing ? (
                <p className="text-[12px] text-amber-700" data-testid={`dist-${key}-need-save`}>
                  Guarda el programa primero para poder subir el archivo.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {d.tiene_instalador && (
                    <div className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 text-[12px]" data-testid={`dist-${key}-actual`}>
                      <span className="truncate text-slate-700">{d.installer_filename || "instalador"}</span>
                      <button type="button" onClick={() => handleRemoveInstaller(key)} disabled={up > 0} data-testid={`dist-${key}-remove`} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 disabled:opacity-50">
                        <Trash2 size={13} /> Quitar
                      </button>
                    </div>
                  )}
                  <label className="inline-flex w-max cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    {up > 0 ? <Loader2 size={15} className="animate-spin" /> : null}
                    {up > 0 ? `Subiendo… ${up}%` : (d.tiene_instalador ? "Reemplazar archivo" : "Seleccionar archivo")}
                    <input type="file" className="hidden" data-testid={`dist-${key}-input`} onChange={(e) => handleInstaller(key, e.target.files?.[0])} disabled={up > 0} />
                  </label>
                </div>
              )}
            </div>
          )}

          <div className="sm:col-span-2">
            <Field label="Requisitos">
              <textarea className={inputCls} rows={2} value={d.requirements} onChange={(e) => setDist(key, "requirements", e.target.value)} data-testid={`dist-${key}-requirements`} placeholder={key === "android" ? "Android 8+ · 2 GB RAM" : "Windows 10/11 · 4 GB RAM"} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Changelog">
              <textarea className={inputCls} rows={2} value={d.changelog} onChange={(e) => setDist(key, "changelog", e.target.value)} data-testid={`dist-${key}-changelog`} />
            </Field>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      data-testid="programa-form-modal"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Editar programa" : "Agregar programa"}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4">
          <h3 className="text-base font-extrabold text-slate-900">
            {editing ? "Editar programa" : "Agregar programa"}
          </h3>
          <button onClick={onClose} data-testid="programa-form-close" aria-label="Cerrar" className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
          <Field label="Nombre *">
            <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="programa-form-name" />
          </Field>
          <Field label="Tipo de programa">
            <select className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="programa-form-type">
              {TIPOS_PROGRAMA.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
            </select>
          </Field>

          <Field label="Descripción oficial">
            <textarea className={inputCls} rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="programa-form-description" />
          </Field>
          <Field label="Descripción funcional breve">
            <textarea className={inputCls} rows={2} value={form.functional_desc} onChange={(e) => set("functional_desc", e.target.value)} data-testid="programa-form-funcdesc" />
          </Field>

          <Field label="Ícono">
            <select className={inputCls} value={form.icon} onChange={(e) => set("icon", e.target.value)} data-testid="programa-form-icon">
              {ICONOS_PROGRAMA.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Imagen (URL, opcional)" hint="Usa http(s). Si se deja vacío, se muestra el ícono.">
            <input className={inputCls} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="programa-form-image" />
          </Field>

          <div className="sm:col-span-2">
            <Field label="Capacidades principales (una por línea)">
              <textarea className={inputCls} rows={4} value={form.capabilities} onChange={(e) => set("capabilities", e.target.value)} data-testid="programa-form-capabilities" />
            </Field>
          </div>

          <Field label="Estado">
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="programa-form-status">
              {ESTADOS_PROGRAMA.map((s) => <option key={s} value={s}>{ESTADO_LABEL[s]}</option>)}
            </select>
          </Field>
          <Field label="Orden de presentación">
            <input type="number" className={inputCls} value={form.display_order} onChange={(e) => set("display_order", e.target.value)} data-testid="programa-form-order" />
          </Field>

          <Field label="Etiqueta (ej. PROGRAMA MAESTRO)">
            <input className={inputCls} value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="programa-form-label" />
          </Field>
          <Field label="URL de aplicación" hint="Se activa el botón Abrir cuando el estado es Disponible/Beta y hay URL.">
            <input className={inputCls} value={form.app_url} onChange={(e) => set("app_url", e.target.value)} data-testid="programa-form-appurl" />
          </Field>

          <Field label="URL de API">
            <input className={inputCls} value={form.api_url} onChange={(e) => set("api_url", e.target.value)} data-testid="programa-form-apiurl" />
          </Field>
          <Field label="URL de documentación">
            <input className={inputCls} value={form.docs_url} onChange={(e) => set("docs_url", e.target.value)} data-testid="programa-form-docsurl" />
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} data-testid="programa-form-featured" />
            Destacado (isFeatured)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} data-testid="programa-form-visible" />
            Visible en la página
          </label>

          {/* PASO 2C · LICENCIA DEL PROGRAMA (nivel programa; licenciable + precios + max_devices editables) */}
          <div className="sm:col-span-2 mt-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="programa-form-licencia">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">Licencia del programa</span>
              <span className="text-[11px] text-slate-400">(única · compartida entre plataformas)</span>
            </div>

            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={!!form.licenciable}
                onChange={(e) => set("licenciable", e.target.checked)}
                data-testid="programa-form-licenciable"
              />
              Programa licenciable (activa la venta de licencias 1/3/6/12 meses)
            </label>

            {form.licenciable && (
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="programa-form-precios">
                {PRECIO_KEYS.map(({ k, label }) => (
                  <Field key={k} label={`${label} (S/)`}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={inputCls}
                      value={preciosSoles[k]}
                      onChange={(e) => setPreciosSoles((p) => ({ ...p, [k]: e.target.value }))}
                      data-testid={`programa-form-precio-${k}`}
                      placeholder="0.00"
                    />
                  </Field>
                ))}
              </div>
            )}

            <div className="mt-3 max-w-xs">
              <Field label="Máximo de dispositivos" hint="Límite total de dispositivos activos por licencia, compartido entre Windows y Android.">
                <input type="number" min="1" className={inputCls} value={form.max_devices} onChange={(e) => set("max_devices", e.target.value)} data-testid="programa-form-maxdevices" />
              </Field>
            </div>
          </div>

          {/* PASO 2B · DISTRIBUCIÓN DEL SOFTWARE (Windows + Android) */}
          <div className="sm:col-span-2 mt-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="programa-form-distribucion">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">Distribución del software</span>
              <span className="text-[11px] text-slate-400">(opcional · Windows + Android)</span>
            </div>
            <p className="mb-3 text-[11px] text-slate-400">La licencia del programa es única y aplica a ambas plataformas.</p>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {PLATFORMS.map(renderPlatform)}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-5 py-4">
          {editing ? (
            <button onClick={handleDelete} disabled={saving} data-testid="programa-form-delete" className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
              <Trash2 size={15} /> Eliminar
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="programa-form-save" className="inline-flex items-center gap-2 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-blue disabled:opacity-60">
              {saving && <Loader2 size={15} className="animate-spin" />}
              {editing ? "Guardar cambios" : "Agregar programa"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
